import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../auth";
import { Icon } from "./icons";

export const STATUS_PT = { present: "Presente", presente: "Presente", justified: "Falta justificada", unjustified: "Falta injustificada", excused: "Dispensado", pending: "Por validar", expected: "Por confirmar", ausente: "Ausência declarada" };
const OPTIONS = [
  { k: "attending", label: "Vou estar presente", short: "Vou", icon: "checkmark", color: "var(--on-success)", bg: "var(--success)" },
  { k: "absent", label: "Não vou estar presente", short: "Não vou", icon: "close-circle", color: "var(--on-warning)", bg: "var(--warning)" },
  { k: "none", label: "Ainda não respondi", short: "Sem resposta", icon: "help", color: "var(--on-surface)", bg: "var(--surface-3)" },
];

/** Singer declaration, shared with the Mobile app: presence = POST /presence, absence = POST /absence, reset = DELETE /absence. */
export async function declare(eventId, status, reason = "") {
  if (status === "absent") return api(`/events/${eventId}/absence`, { method: "POST", body: JSON.stringify({ reason }) });
  if (status === "attending") return api(`/events/${eventId}/presence`, { method: "POST" });
  try {
    return await api(`/events/${eventId}/absence`, { method: "DELETE" });
  } catch (err) {
    if (err.status === 404) return {};
    if (err.status === 409) throw new Error("O servidor ainda não permite voltar a «sem resposta» depois de responder. Escolha «vou» ou «não vou».");
    throw err;
  }
}

export const declaredOf = (rec, event) => {
  const att = event?.my_attendance;
  if (rec?.declared_status || att?.declared_status) return rec?.declared_status || att.declared_status;
  if (att?.status === "pending" || att?.status === "ausente" || att?.reason) return "absent";
  return "none";
};

export function PresenceCard({ event, compact }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [msg, setMsg] = useState("");
  const [local, setLocal] = useState(null);
  const me = useQuery({ queryKey: ["me-attendance"], queryFn: () => api("/me/attendance"), enabled: !compact });
  const rec = (me.data?.records || []).find((r) => r.event?.id === event.id);
  const convoked = (event.members || []).some((m) => m.id === user?.id) || !!rec || !!event.my_attendance;
  const m = useMutation({
    mutationFn: ({ status, reason }) => declare(event.id, status, reason),
    onSuccess: (r, v) => {
      setLocal(v.status);
      setMsg("Resposta guardada.");
      ["me-attendance", "home", "events"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries({ queryKey: ["event", event.id] });
    },
    onError: (e) => setMsg(e.message),
  });
  if (user?.is_singer === false || !convoked) return null;
  const past = new Date(event.starts_at) < new Date();
  const declared = local || declaredOf(rec, event);
  const classified = rec?.validated ? STATUS_PT[rec.validated_status] || rec.validated_status : null;
  const buttons = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} role="radiogroup" aria-label="Declaração de presença">
      {OPTIONS.map((o) => {
        const on = declared === o.k;
        return (
          <button key={o.k} role="radio" aria-checked={on} disabled={past || m.isPending} className={`chip ${on ? "active" : ""}`} onClick={() => pick(o.k)} data-testid={`presence-${o.k}${compact ? `-${event.id}` : ""}`}
            style={on ? { background: o.bg, borderColor: o.bg, color: o.color } : undefined}>
            <Icon name={o.icon} size={14} /> {(compact ? o.short : o.label).toUpperCase()}
          </button>
        );
      })}
    </div>
  );
  if (compact) {
    if (past) return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid={`calendar-presence-${event.id}`}>
        {buttons}
        {msg ? <div className="t-hint" style={{ color: "var(--on-surface)" }} data-testid={`calendar-presence-msg-${event.id}`}>{msg}</div> : null}
      </div>
    );
  }
  function pick(k) {
    if (k === "absent") {
      const r = window.prompt("Motivo da ausência (opcional, só visível para a direcção):", rec?.reason || event.my_attendance?.reason || "");
      if (r === null) return;
      m.mutate({ status: k, reason: r });
    } else m.mutate({ status: k });
  }
  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }} data-testid="presence-card">
      <div className="t-section">A minha presença</div>
      {buttons}
      {past ? <div className="t-hint">Evento terminado — a declaração já não pode ser alterada.</div> : null}
      {msg ? <div className="t-hint" data-testid="presence-msg" style={{ color: "var(--on-surface)" }}>{msg}</div> : null}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><div className="t-hint">Declaração do cantor</div><div data-testid="presence-declared">{OPTIONS.find((o) => o.k === declared)?.label}</div></div>
        <div><div className="t-hint">Classificação da direcção</div><div data-testid="presence-classified">{classified || "Por classificar"}</div></div>
      </div>
    </div>
  );
}
